import React from "react";
import PropTypes from "prop-types";
import { useDroppable } from "@dnd-kit/core";
import KanbanCard from "./KanbanCard";

// Derive a colour accent class from the lane name / type
function laneAccent(lane) {
  if (lane.is_error_lane) return "accent-red";
  const n = (lane.name || "").toLowerCase();
  if (n.includes("approv") || n.includes("complete") || n.includes("done"))  return "accent-green";
  if (n.includes("progress") || n.includes("review") || n.includes("active")) return "accent-primary";
  if (n.includes("pending") || n.includes("submitted") || n.includes("new"))  return "accent-blue";
  if (n.includes("hold") || n.includes("paused") || n.includes("waiting"))    return "accent-amber";
  if (n.includes("reject") || n.includes("cancel") || n.includes("failed"))   return "accent-red";
  if (n.includes("sap") || n.includes("system") || n.includes("integrat"))    return "accent-navy";
  // For stage-based boards: give each a neutral grey default (overridden above)
  return "accent-grey";
}

function formatValue(val) {
  if (!val || val <= 0) return null;
  if (val >= 1e7) return `₹${(val / 1e7).toFixed(1)}Cr`;
  if (val >= 1e5) return `₹${(val / 1e5).toFixed(1)}L`;
  return `₹${val.toLocaleString("en-IN")}`;
}

export default function KanbanLane({ lane, collapsed, onToggleCollapse, onAction, onCardClick, chatUnread }) {
  const laneKey = String(lane.lane_id ?? lane.stage_id);
  const { setNodeRef, isOver } = useDroppable({ id: laneKey, disabled: !lane.stage_id });

  const cardCount  = (lane.cards || []).length;
  const valueLabel = formatValue(lane.total_value);
  const accent     = laneAccent(lane);

  // ── Collapsed pill ────────────────────────────────────────────────
  if (collapsed) {
    return (
      <div
        className="kb-lane-collapsed"
        onClick={onToggleCollapse}
        title={`Expand ${lane.name}`}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === "Enter" && onToggleCollapse()}
      >
        <span className="kb-lane-collapsed-name">{lane.name}</span>
        <span className="kb-lane-collapsed-count">{cardCount}</span>
      </div>
    );
  }

  // ── Full lane ─────────────────────────────────────────────────────
  return (
    <div
      className={[
        "kb-lane",
        accent,
        isOver            ? "is-over"       : "",
        lane.is_error_lane ? "is-error-lane" : "",
      ].filter(Boolean).join(" ")}
    >
      {/* Lane header */}
      <div className="kb-lane-header">
        <div className="d-flex align-items-center">
          {lane.is_error_lane && (
            <i
              className="bi bi-exclamation-triangle-fill me-2"
              style={{ fontSize: 12, color: "var(--t2t-danger)" }}
            />
          )}
          <span className="kb-lane-name">{lane.name}</span>
          <span className="kb-lane-count">{cardCount}</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          {valueLabel && (
            <span className="kb-lane-value">{valueLabel}</span>
          )}
          <button
            className="kb-lane-collapse-btn"
            onClick={onToggleCollapse}
            title="Collapse lane"
            aria-label="Collapse lane"
          >
            <i className="bi bi-chevron-double-left" />
          </button>
        </div>
      </div>

      {/* Drop zone + cards */}
      <div
        ref={setNodeRef}
        className={`kb-lane-dropzone${isOver ? " is-over" : ""}`}
      >
        {cardCount === 0 ? (
          <div className="kb-lane-empty">
            <i className="bi bi-inbox kb-lane-empty-icon" />
            <span className="kb-lane-empty-text">Drop cards here</span>
          </div>
        ) : (
          (lane.cards || []).map(card => (
            <KanbanCard
              key={card.request_id}
              card={card}
              laneId={lane.stage_id}
              onAction={onAction}
              onCardClick={onCardClick}
              chatUnreadCount={chatUnread ? (chatUnread[card.request_id] || 0) : 0}
            />
          ))
        )}
      </div>
    </div>
  );
}

KanbanLane.propTypes = {
  lane: PropTypes.shape({
    lane_id:       PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    stage_id:      PropTypes.number,
    name:          PropTypes.string,
    cards:         PropTypes.array,
    total_value:   PropTypes.number,
    is_error_lane: PropTypes.bool,
  }).isRequired,
  collapsed:        PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  onAction:         PropTypes.func,
  onCardClick:      PropTypes.func,
  chatUnread:       PropTypes.object,
};
